import { useState, useEffect, useCallback } from "react";
import {
  getPredicateManifest,
  getEntityFacts,
  updateEntityFact,
  deleteEntityFact,
  type PredicateDefinition,
  type EntityFact,
} from "@/lib/api/endpoints/entity-facts";

interface ContactFactsSectionProps {
  contactId: string;
  authToken: string | null;
}

const VALUE_TYPE_LABELS: Record<string, string> = {
  string: "Text",
  text: "Text",
  date: "Date",
  datetime: "Date/Time",
  email: "Email",
  phone: "Phone",
  address: "Address",
  url: "URL",
  enum: "Category",
  entity_ref: "Reference",
  json: "Data",
  number: "Number",
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  user_stated: "User stated",
  user_edit: "User edited",
  email_ingested: "From email",
  calendar_ingested: "From calendar",
  derived_from_patterns: "Detected",
  inferred: "Inferred",
  external_research: "Researched",
};

const HIDDEN_PREDICATES = new Set([
  "name",
  "xavior:communication_profile",
  "xavior:cadence_label",
  "xavior:relationship_label",
]);

export function ContactFactsSection({
  contactId,
  authToken,
}: ContactFactsSectionProps) {
  const [predicates, setPredicates] = useState<PredicateDefinition[]>([]);
  const [facts, setFacts] = useState<EntityFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFact, setEditingFact] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [collapsed, setCollapsed] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [preds, entityFacts] = await Promise.all([
        getPredicateManifest(authToken, "Contact"),
        getEntityFacts(authToken, contactId),
      ]);
      setPredicates(preds.filter((p) => !HIDDEN_PREDICATES.has(p.id)));
      setFacts(entityFacts);
    } catch {
      // silently fail — facts section is supplementary
    } finally {
      setLoading(false);
    }
  }, [authToken, contactId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSave = async (factId: string) => {
    try {
      await updateEntityFact(authToken, factId, editValue);
      setEditingFact(null);
      await loadData();
    } catch {
      /* display will refresh on next load */
    }
  };

  const handleDelete = async (factId: string) => {
    try {
      await deleteEntityFact(authToken, factId);
      await loadData();
    } catch {
      /* best effort */
    }
  };

  if (loading) return null;
  if (facts.length === 0 && predicates.length === 0) return null;

  const factsByPredicate = new Map<string, EntityFact[]>();
  for (const fact of facts) {
    const list = factsByPredicate.get(fact.predicateId) ?? [];
    list.push(fact);
    factsByPredicate.set(fact.predicateId, list);
  }

  const predicatesWithFacts = predicates.filter((p) =>
    factsByPredicate.has(p.id),
  );
  if (predicatesWithFacts.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div
        className="flex items-center gap-2 mb-2 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text-secondary, #5f6368)",
          }}
        >
          Structured Details
        </span>
        <span
          style={{ fontSize: 11, color: "var(--color-text-tertiary, #80868b)" }}
        >
          ({predicatesWithFacts.length} fields)
        </span>
        <span className="text-xs text-gray-400">
          {collapsed ? "\u25B8" : "\u25BE"}
        </span>
      </div>

      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {predicatesWithFacts.map((pred) => {
            const predFacts = factsByPredicate.get(pred.id) ?? [];
            return (
              <div
                key={pred.id}
                style={{
                  padding: "8px 12px",
                  borderRadius: 6,
                  background: "var(--color-surface-secondary, #f8f9fa)",
                  border: "1px solid var(--color-border-light, #e8eaed)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--color-text-tertiary, #80868b)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {pred.description || pred.name}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--color-text-tertiary, #aaa)",
                    }}
                  >
                    {VALUE_TYPE_LABELS[pred.valueType] || pred.valueType}
                  </span>
                </div>
                {predFacts.map((fact) => (
                  <div
                    key={fact.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    {editingFact === fact.id ? (
                      <>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void handleSave(fact.id);
                            if (e.key === "Escape") setEditingFact(null);
                          }}
                          autoFocus
                          style={{
                            flex: 1,
                            padding: "4px 8px",
                            fontSize: 13,
                            borderRadius: 4,
                            border:
                              "1px solid var(--color-border-focus, #1a73e8)",
                            outline: "none",
                            background: "var(--color-surface, #fff)",
                            color: "var(--color-text-primary, #202124)",
                          }}
                        />
                        <button
                          onClick={() => void handleSave(fact.id)}
                          style={{
                            fontSize: 11,
                            color: "var(--color-action-save-bg, #1a73e8)",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingFact(null)}
                          style={{
                            fontSize: 11,
                            color: "var(--color-text-tertiary, #80868b)",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontSize: 13 }}>
                          {pred.valueType === "url" &&
                          isValidUrl(fact.value) ? (
                            <a
                              href={normalizeUrl(fact.value)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "var(--color-link, #1a73e8)",
                                textDecoration: "none",
                              }}
                            >
                              {formatFactValue(fact, pred)}
                            </a>
                          ) : (
                            <span
                              style={{
                                color: "var(--color-text-primary, #202124)",
                              }}
                            >
                              {formatFactValue(fact, pred)}
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: "var(--color-text-tertiary, #aaa)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {SOURCE_TYPE_LABELS[fact.sourceType] ||
                            fact.sourceType}
                        </span>
                        <button
                          onClick={() => {
                            setEditingFact(fact.id);
                            setEditValue(fact.value);
                          }}
                          style={{
                            fontSize: 11,
                            color: "var(--color-text-tertiary, #80868b)",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: "2px 4px",
                          }}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => void handleDelete(fact.id)}
                          style={{
                            fontSize: 11,
                            color: "var(--color-error, #d93025)",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: "2px 4px",
                          }}
                          title="Delete"
                        >
                          &times;
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function isValidUrl(value: string): boolean {
  try {
    const url = value.startsWith("http") ? value : `https://${value}`;
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function normalizeUrl(value: string): string {
  if (value.startsWith("http")) return value;
  return `https://${value}`;
}

function formatFactValue(fact: EntityFact, pred: PredicateDefinition): string {
  if (pred.valueType === "date" && fact.value.startsWith("--")) {
    const [, mm, dd] = fact.value.match(/^--(\d{2})-(\d{2})$/) || [];
    if (mm && dd) {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${monthNames[parseInt(mm, 10) - 1]} ${parseInt(dd, 10)}`;
    }
  }
  if (pred.valueType === "date" && /^\d{4}-\d{2}-\d{2}$/.test(fact.value)) {
    return new Date(fact.value + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return fact.value;
}
